import React, { FC, useRef } from 'react';
import {
  SideSheetBody,
  SideSheetContentCol,
  Box,
  Text,
  Spinner,
  SpinnerSize,
  Button,
  ButtonSize,
  Icon,
  theme,
} from '@bechtel/gds';
import {
  DrawerBackdropStyled,
  DrawerOuterStyled,
  DrawerHeaderStyled,
  DrawerCloseStyled,
  ProjectTileStyled,
  ProjectImageStyled,
  ButtonStyled,
} from './styled';
import { Project, SetUserDefaultProjectInput, SetUserDefaultProjectPayload } from '@bechtel/types';
//@ts-ignore images ignore
import { ReactComponent as BechtelLogo } from '@bechtel/client-assets/dist/images/bechtel-logo.svg';
import { GET_PROJECTS, UPDATE_USER_PROJECT } from '@bechtel/client-gql';
import { NetworkStatus, useQuery, useMutation, useApolloClient } from '@apollo/client';
import { ErrorBoundary } from '~/components/atoms/error-boundary';
import { EMULATED_USER_ID, USER_PROJECT_ID } from '~/components/atoms/cache-keys';
import { removeCacheKeys } from '~/utilities/session-storage';
import { useNavigate } from 'react-router-dom';
import { find, get } from 'lodash';
import { t, Trans } from '@lingui/macro';

interface ProjectTileProp {
  item: Project;
  selected?: boolean;
  onSelectProject: (projectId: string) => void;
  saving: boolean;
}
export const ProjectTile: FC<ProjectTileProp> = ({ item, selected, onSelectProject, saving }) => {
  const currentProjectWeek = find(item?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  return (
    <ProjectTileStyled data-testid={`project-list-${item.id}`} selected={selected}>
      <ProjectImageStyled selected={selected}>
        {item.logoAttachment && <img src={item.logoAttachment.url} />}
        {!item.logoAttachment && <BechtelLogo />}
      </ProjectImageStyled>
      <Box flexDirection={{ sm: 'column' }} p={{ sm: '500' }}>
        <Text data-testid={`project-name-${item.id}`} size="500" weight="600" color="grey.600" family="graphik">
          {item.name}
        </Text>
        <Text
          size="400"
          color="grey.600"
          family="graphik"
          data-testid={`project-location-${item.id}`}
          style={{ paddingTop: `${theme.spacings[100]}` }}
        >
          {item.location}, {item.state.name}, {item.country.name}
        </Text>
        <Text
          size="300"
          color="grey.600"
          family="graphik"
          data-testid={`project-dept-${item.id}`}
          style={{ paddingTop: `${theme.spacings[450]}` }}
        >
          {item.gbu.description}
        </Text>
        <Text
          size="300"
          color="grey.600"
          family="graphik"
          data-testid={`project-current-week-${item.id}`}
          style={{ paddingTop: `${theme.spacings[100]}` }}
        >
          {t`Week`} {currentProjectWeek?.weekNumber} of {item.projectConfiguration.totalWeeks}
        </Text>
        <ButtonStyled
          data-testid={`${selected ? 'selected' : 'select'}-project-${item.id}`}
          disabled={selected}
          onClick={() => onSelectProject(item.id)}
        >
          {!selected && saving ? (
            <Spinner size={SpinnerSize.MEDIUM}></Spinner>
          ) : selected ? (
            t`Selected Project`
          ) : (
            t`Select Project`
          )}
        </ButtonStyled>
      </Box>
    </ProjectTileStyled>
  );
};

export const ProjectListing = ({ close }) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const selectedProjectId = useRef<string>();
  const { loading, error, data, refetch, networkStatus } = useQuery<{ projects: Project[] }>(GET_PROJECTS);
  const [setUserDefaultProject, { loading: saving }] = useMutation<
    { setUserDefaultProject: SetUserDefaultProjectPayload },
    { input: SetUserDefaultProjectInput }
  >(UPDATE_USER_PROJECT, {
    onCompleted: async (data) => {
      //TODO: Backend returning incorrect projectid in the mutation. Enable it after it is fixed
      // const userProjectId = get(data, 'setUserDefaultProject.user.project.id', '');
      //Temporary fix using useRef to hold the value of the selectedproject
      if (!selectedProjectId.current) return;
      removeCacheKeys();
      window.sessionStorage.setItem(EMULATED_USER_ID, '');
      window.sessionStorage.setItem(USER_PROJECT_ID, selectedProjectId.current.toString());
      navigate('/', { replace: true });
      close();
      await client.resetStore();
    },
    onError: (error) => {
      //TODO: Only one error enum is there. confirm with UX
    },
  });

  const userProjectId = window.sessionStorage.getItem(USER_PROJECT_ID);

  return (
    <DrawerBackdropStyled>
      <DrawerOuterStyled>
        <DrawerHeaderStyled title={t`All Projects`}>
          <DrawerCloseStyled onClick={close}></DrawerCloseStyled>
        </DrawerHeaderStyled>
        <SideSheetBody>
          <ErrorBoundary>
            <SideSheetContentCol>
              <Box p={{ sm: '400' }} flexDirection={{ sm: 'column' }}>
                {(loading || networkStatus === NetworkStatus.refetch) && (
                  <Box css="height: 100%;" data-testid="loading">
                    <Box
                      flexGrow={{ sm: 1 }}
                      alignItems={{ sm: 'center' }}
                      flexDirection={{ sm: 'column' }}
                      justifyContent={{ sm: 'center' }}
                    >
                      <Spinner size={SpinnerSize.LARGE} />
                    </Box>
                  </Box>
                )}
                {!loading && error && (
                  <Box css="height: 100%;" data-testid="retry">
                    <Box
                      flexGrow={{ sm: 1 }}
                      alignItems={{ sm: 'center' }}
                      flexDirection={{ sm: 'column' }}
                      justifyContent={{ sm: 'center' }}
                    >
                      <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
                        <Text>
                          <Trans>There was a problem loading projects</Trans>
                        </Text>
                        <Text>
                          <Trans>Please click the button below to try again.</Trans>
                        </Text>
                      </Box>
                      <Box>
                        <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
                          <Box mr={{ sm: '400' }}>
                            <Icon icon="undo" />
                          </Box>
                          <Trans>Retry</Trans>
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
                {!loading && !error && data?.projects && data.projects.length === 0 && (
                  <Box p={{ sm: '450' }} justifyContent={{ sm: 'center' }} data-testid="no-material-found">
                    <Trans> No project found.</Trans>
                  </Box>
                )}
                {!loading && !error && data && data.projects && data.projects.length > 0 && (
                  <>
                    {data.projects.map((item, index) => (
                      <ProjectTile
                        item={item}
                        key={index}
                        selected={item.id === userProjectId}
                        saving={saving}
                        onSelectProject={async () => {
                          selectedProjectId.current = item.id;
                          await setUserDefaultProject({
                            variables: {
                              input: { projectId: selectedProjectId.current },
                            },
                          });
                        }}
                      />
                    ))}
                  </>
                )}
              </Box>
            </SideSheetContentCol>
          </ErrorBoundary>
        </SideSheetBody>
      </DrawerOuterStyled>
    </DrawerBackdropStyled>
  );
};
